/**
 * Supplier Feed Admin UI — FoodPulse.
 *
 * Route : /admin/foodpulse/supplier-feeds
 * Access: admin only (relies on /api/supplier-feeds admin endpoints).
 *
 * Panels
 *  1. Left column: list of feeds with mode badge + last-run status.
 *  2. Right column: selected feed detail — supplier name, transport,
 *     URL/auth header, field mapper (source→canonical), mode toggle,
 *     dry-run preview, run now, and staging queue with approve/reject.
 *  3. Modal to add a new feed.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw, Loader2, Play, TestTube, Trash2, Save, ArrowLeftRight, Check, X, Zap, FileJson, FileText, Webhook } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";
const AMBER = "#E4A140";

const TRANSPORT_ICONS = { json_pull: FileJson, csv_pull: FileText, webhook: Webhook };
const TRANSPORTS = [
  { value: "json_pull", label: "JSON pull (HTTPS GET)" },
  { value: "csv_pull",  label: "CSV pull (HTTPS GET)"  },
  { value: "webhook",   label: "Webhook (supplier POSTs to us)" },
];
const FIELD_KEYS = ["title", "description", "gtin", "product_name", "product_category", "deal_price", "original_price", "discount_pct", "unit", "quantity_available", "starts_at", "ends_at", "image_url"];

function ModeBadge({ mode }) {
  return <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: mode === "auto_publish" ? "#BBF7D0" : "#FDE68A", color: GREEN }}>{mode === "auto_publish" ? "AUTO-PUBLISH" : "STAGING"}</span>;
}

function AddFeedModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ supplier_name: "", business_id: "", transport: "json_pull", url: "", auth_header: "", mode: "staging" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [businesses, setBusinesses] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(async () => {
      try {
        const r = await http.get("/food-businesses", { params: { q: q || undefined, limit: 20 } });
        setBusinesses(r.data.items || []);
      } catch { /* noop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);
  const submit = async () => {
    setBusy(true); setError("");
    try { const r = await http.post("/supplier-feeds", form); onCreated(r.data); onClose(); }
    catch (e) { setError(e.response?.data?.detail || "Create failed"); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-20 px-4" onClick={onClose} data-testid="add-feed-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold" style={{ color: GREEN }}>Add supplier feed</h3>
          <button onClick={onClose} className="text-gray-500" data-testid="close-add-modal"><X size={18} /></button>
        </div>
        <div className="grid gap-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Supplier name</label>
            <input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="supplier-name-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Business (search)</label>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type to filter…" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="business-search-input" />
            <select value={form.business_id} onChange={(e) => setForm({ ...form, business_id: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="business-picker">
              <option value="">Select a business…</option>
              {businesses.map((b) => <option key={b.id} value={b.id}>{b.name} — {b.city || "—"}, {b.state || ""}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Transport</label>
            <select value={form.transport} onChange={(e) => setForm({ ...form, transport: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="transport-select">
              {TRANSPORTS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {form.transport !== "webhook" && (
            <>
              <div>
                <label className="text-xs font-semibold text-gray-600">Feed URL</label>
                <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://supplier.example.com/feed.json" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="feed-url-input" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600">Authorization header (optional)</label>
                <input value={form.auth_header} onChange={(e) => setForm({ ...form, auth_header: e.target.value })} placeholder="Bearer XYZ" className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="auth-header-input" />
              </div>
            </>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-600">Mode</label>
            <div className="flex gap-2 mt-1">
              {["staging", "auto_publish"].map((m) => (
                <button key={m} onClick={() => setForm({ ...form, mode: m })} className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold border ${form.mode === m ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={form.mode !== m ? { borderColor: GREEN, color: GREEN } : {}} data-testid={`mode-${m}`}>
                  {m === "auto_publish" ? "Auto publish" : "Staging (safer)"}
                </button>
              ))}
            </div>
          </div>
          {error && <div className="text-sm text-red-700" data-testid="add-feed-error">{error}</div>}
          <button onClick={submit} disabled={busy || !form.supplier_name || !form.business_id} className="rounded-full py-2 font-semibold text-sm disabled:opacity-60" style={{ background: GREEN, color: CREAM }} data-testid="create-feed-btn">
            {busy ? <Loader2 className="animate-spin inline" size={14} /> : "Create feed"}
          </button>
        </div>
      </div>
    </div>
  );
}

function FeedDetail({ feed, onChanged }) {
  const [fieldMap, setFieldMap] = useState(feed.field_map || {});
  const [mode, setMode] = useState(feed.mode);
  const [url, setUrl] = useState(feed.url || "");
  const [auth, setAuth] = useState(feed.auth_header || "");
  const [preview, setPreview] = useState(null);
  const [staging, setStaging] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setFieldMap(feed.field_map || {}); setMode(feed.mode); setUrl(feed.url || ""); setAuth(feed.auth_header || "");
    setPreview(null);
    loadStaging();
    // eslint-disable-next-line
  }, [feed.id]);

  const loadStaging = async () => {
    try { const r = await http.get(`/supplier-feeds/${feed.id}/staging`); setStaging(r.data.items || []); } catch { setStaging([]); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await http.patch(`/supplier-feeds/${feed.id}`, { field_map: fieldMap, mode, url, auth_header: auth });
      onChanged();
    } catch (e) { alert(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const dryRun = async () => {
    setBusy(true); setPreview(null);
    try { const r = await http.post(`/supplier-feeds/${feed.id}/dry-run`); setPreview(r.data); }
    catch (e) { alert(e.response?.data?.detail || "Dry-run failed"); }
    setBusy(false);
  };
  const runNow = async () => {
    setBusy(true);
    try { const r = await http.post(`/supplier-feeds/${feed.id}/run`); alert(`Run OK — stats ${JSON.stringify(r.data.stats)}`); loadStaging(); onChanged(); }
    catch (e) { alert(e.response?.data?.detail || "Run failed"); }
    setBusy(false);
  };
  const archive = async () => {
    if (!window.confirm(`Archive "${feed.supplier_name}"?`)) return;
    await http.delete(`/supplier-feeds/${feed.id}`); onChanged();
  };
  const approveRow = async (id) => { await http.post(`/supplier-feeds/staging/${id}/approve`); loadStaging(); };
  const rejectRow  = async (id) => { await http.post(`/supplier-feeds/staging/${id}/reject`);  loadStaging(); };

  return (
    <div data-testid="feed-detail">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <div className="text-lg font-bold" style={{ color: GREEN }}>{feed.supplier_name}</div>
          <div className="text-xs text-gray-600">Transport: <b>{feed.transport}</b> · Last run: {feed.last_run_at ? new Date(feed.last_run_at).toLocaleString() : "never"} ({feed.last_run_status || "—"})</div>
        </div>
        <div className="flex flex-wrap gap-2">
          {feed.transport !== "webhook" && (
            <>
              <button onClick={dryRun} disabled={busy} className="rounded-full border px-3 py-2 text-xs font-semibold" style={{ borderColor: GREEN, color: GREEN }} data-testid="dry-run-btn"><TestTube size={12} className="inline mr-1" />Dry-run</button>
              <button onClick={runNow} disabled={busy} className="rounded-full px-3 py-2 text-xs font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="run-now-btn"><Play size={12} className="inline mr-1" />Run now</button>
            </>
          )}
          <button onClick={archive} className="rounded-full border px-3 py-2 text-xs font-semibold" style={{ borderColor: "#b91c1c", color: "#b91c1c" }} data-testid="archive-btn"><Trash2 size={12} className="inline mr-1" />Archive</button>
        </div>
      </div>

      {feed.transport === "webhook" && (
        <div className="rounded-xl border p-3 mb-4 text-xs" style={{ borderColor: "#E9D8B4", background: "#FEF9E7" }} data-testid="webhook-hint">
          <div className="font-semibold" style={{ color: GREEN }}>Webhook endpoint</div>
          <code className="block break-all mt-1">POST /api/supplier-feeds/{feed.feed_key}/webhook</code>
          <div className="mt-1">Sign the raw body with HMAC-SHA256 using <b>hmac_secret</b> from feed detail (rotate any time). Send hex digest in <code>X-FP-Signature</code> header.</div>
        </div>
      )}

      {feed.transport !== "webhook" && (
        <div className="grid gap-3 mb-4">
          <div>
            <label className="text-xs font-semibold text-gray-600">Feed URL</label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="edit-url-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-600">Auth header</label>
            <input value={auth} onChange={(e) => setAuth(e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm mt-1" style={{ borderColor: "#E9D8B4" }} data-testid="edit-auth-input" />
          </div>
        </div>
      )}

      <div className="mb-4">
        <label className="text-xs font-semibold text-gray-600 mb-2 block">Field mapping (source column/key → FoodPulse field)</label>
        <div className="grid grid-cols-2 gap-2">
          {FIELD_KEYS.map((k) => (
            <div key={k} className="flex items-center gap-2">
              <ArrowLeftRight size={12} className="text-gray-400" />
              <div className="text-xs w-32 truncate" style={{ color: GREEN }}>{k}</div>
              <input value={fieldMap[k] || ""} onChange={(e) => setFieldMap({ ...fieldMap, [k]: e.target.value })} placeholder={k} className="flex-1 border rounded px-2 py-1 text-xs" style={{ borderColor: "#E9D8B4" }} data-testid={`fieldmap-${k}`} />
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <span className="text-xs font-semibold text-gray-600">Mode:</span>
        <button onClick={() => setMode("staging")} className={`rounded-full px-3 py-1 text-xs font-semibold border ${mode === "staging" ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={mode !== "staging" ? { borderColor: GREEN, color: GREEN } : {}} data-testid="mode-staging-btn">Staging</button>
        <button onClick={() => setMode("auto_publish")} className={`rounded-full px-3 py-1 text-xs font-semibold border ${mode === "auto_publish" ? "bg-[#0F3D2E] text-[#FBF7EF]" : ""}`} style={mode !== "auto_publish" ? { borderColor: GREEN, color: GREEN } : {}} data-testid="mode-auto-btn">Auto-publish</button>
        <button onClick={save} disabled={busy} className="ml-auto rounded-full px-3 py-2 text-xs font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="save-feed-btn"><Save size={12} className="inline mr-1" />Save</button>
      </div>

      {preview && (
        <div className="rounded-xl border p-3 mb-4" style={{ borderColor: "#E9D8B4", background: "#F7F2E4" }} data-testid="dry-run-preview">
          <div className="text-xs font-semibold mb-2" style={{ color: GREEN }}>Preview — {preview.count} rows, first 5 shown</div>
          <pre className="text-[10px] overflow-x-auto max-h-64">{JSON.stringify(preview.preview, null, 2)}</pre>
        </div>
      )}

      {staging.length > 0 && (
        <div data-testid="staging-list">
          <div className="text-sm font-semibold mb-2" style={{ color: GREEN }}>Staging queue ({staging.length})</div>
          <div className="grid gap-2">
            {staging.map((s) => (
              <div key={s.id} className="flex items-center justify-between border rounded-xl p-2 text-xs bg-white" style={{ borderColor: "#E9D8B4" }} data-testid={`staging-row-${s.id}`}>
                <div>
                  <div className="font-semibold" style={{ color: GREEN }}>{s.payload.title}</div>
                  <div className="text-[10px] text-gray-500">{s.payload.product_gtin || "no gtin"} · ${s.payload.deal_price} · {s.payload.product_category || "—"}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => approveRow(s.id)} className="rounded-full px-2 py-1 text-[10px] font-bold" style={{ background: GREEN, color: CREAM }} data-testid={`approve-${s.id}`}><Check size={10} className="inline" /></button>
                  <button onClick={() => rejectRow(s.id)}  className="rounded-full border px-2 py-1 text-[10px] font-bold" style={{ borderColor: "#b91c1c", color: "#b91c1c" }} data-testid={`reject-${s.id}`}><X size={10} className="inline" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function SupplierFeedsAdmin() {
  const { user, loading: authLoading } = useAuth();
  const [feeds, setFeeds] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await http.get("/supplier-feeds"); setFeeds(r.data.items || []); if (!selectedId && r.data.items?.[0]) setSelectedId(r.data.items[0].id); }
    catch { setFeeds([]); }
    setLoading(false);
  }, [selectedId]);
  useEffect(() => { if (user) load(); }, [user, load]);
  const selected = useMemo(() => feeds.find((f) => f.id === selectedId), [feeds, selectedId]);

  if (authLoading) return <div className="p-10 text-center"><Loader2 className="animate-spin mx-auto" /></div>;
  if (!user?.is_admin && !user?.platform_role) return <div className="p-10 text-center">Admin access required.</div>;

  return (
    <main className="min-h-screen" style={{ background: CREAM }} data-testid="supplier-feeds-admin">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <header className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-black flex items-center gap-2" style={{ color: GREEN }}><Zap /> Supplier Feeds</h1>
            <p className="text-sm text-gray-600">Ingest daily deals from supermarkets, wholesalers, distributors, and grocery chains.</p>
          </div>
          <div className="flex gap-2">
            <button onClick={load} className="rounded-full border px-3 py-2 text-xs" style={{ borderColor: GREEN, color: GREEN }} data-testid="reload-feeds-btn"><RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={() => setShowAdd(true)} className="rounded-full px-4 py-2 text-sm font-semibold" style={{ background: GREEN, color: CREAM }} data-testid="add-feed-btn"><Plus size={14} className="inline mr-1" />Add feed</button>
          </div>
        </header>

        <div className="grid md:grid-cols-3 gap-6">
          <aside className="md:col-span-1">
            <div className="rounded-2xl bg-white border overflow-hidden" style={{ borderColor: "#E9D8B4" }}>
              {feeds.length === 0 ? (
                <div className="p-6 text-center text-sm text-gray-600" data-testid="feeds-empty">No feeds yet. Click <b>Add feed</b> to create one.</div>
              ) : feeds.map((f) => {
                const Icon = TRANSPORT_ICONS[f.transport] || FileJson;
                return (
                  <button key={f.id} onClick={() => setSelectedId(f.id)} className={`w-full text-left p-3 border-b flex items-center gap-2 ${selectedId === f.id ? "bg-[#F7F2E4]" : ""}`} style={{ borderColor: "#F5E6C4" }} data-testid={`feed-row-${f.id}`}>
                    <Icon size={16} style={{ color: GREEN }} />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate" style={{ color: GREEN }}>{f.supplier_name}</div>
                      <div className="text-[10px] text-gray-500">{f.transport} · {f.active ? "active" : "archived"}</div>
                    </div>
                    <ModeBadge mode={f.mode} />
                  </button>
                );
              })}
            </div>
          </aside>
          <section className="md:col-span-2 rounded-2xl bg-white border p-5" style={{ borderColor: "#E9D8B4" }}>
            {selected ? <FeedDetail feed={selected} onChanged={load} /> : <div className="text-center py-10 text-gray-500">Select a feed to configure.</div>}
          </section>
        </div>

        {showAdd && <AddFeedModal onClose={() => setShowAdd(false)} onCreated={(f) => { load(); setSelectedId(f.id); }} />}
      </div>
    </main>
  );
}
